import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, Star, Lightbulb } from "lucide-react";
import Navigation from "@/components/Navigation";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 lg:px-12" data-testid="about-hero">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Profile */}
            <div className="order-2 lg:order-1">
              <div className="flex items-start gap-8">
                <div className="profile-container flex-shrink-0" data-testid="about-profile-image">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_2eed5c8c-c903-4cd4-a6ee-5dc5c12a3bca/artifacts/oyfd26yh_IMG_5012.jpeg" 
                    alt="Brian Bass - BriVonB - Today"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div>
                  <p className="text-sm font-medium uppercase tracking-widest text-[#4FB3D4] mb-4">
                    Meet Your Guide
                  </p>
                  <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight leading-tight text-[#1A2A3A] mb-6">
                    Hi, I'm <span className="text-[#4FB3D4]">Brian Bass</span>
                  </h1>
                </div>
              </div>
              
              <div className="mt-8 space-y-6 text-lg text-[#1A2A3A]/70 leading-relaxed">
                <p>
                  I understand the pain, confusion, and isolation that comes from being in a narcissistic or abusive relationship. I've been there. And I want you to know—<strong className="text-[#1A2A3A]">there is a way out, and there is hope</strong>.
                </p>
                <p>
                  After years of my own recovery journey, I've dedicated my life to helping others break free from toxic relationships and rediscover their true worth. This platform is the resource I wish I had when I was starting my healing process.
                </p>
                <p>
                  My approach combines evidence-based techniques with compassionate support, because I know that healing isn't linear—and you deserve patience and understanding every step of the way.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -top-8 -right-8 w-64 h-64 bg-[#DCD6F7] rounded-full opacity-40 blur-3xl" />
                <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-[#4FB3D4] rounded-full opacity-30 blur-3xl" />
                <img 
                  src="https://images.pexels.com/photos/6942996/pexels-photo-6942996.jpeg"
                  alt="Ocean waves representing calm and healing"
                  className="rounded-3xl shadow-floating relative z-10 w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After - Brian's Real Photos */}
      <section className="py-20 px-6 lg:px-12 bg-white" data-testid="before-after-photos">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-4">The Real Me</p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#1A2A3A]">
              This Is What Recovery Looks Like
            </h2>
            <p className="text-base text-[#1A2A3A]/60 mt-4 max-w-xl mx-auto">
              These are real photos of me, Brian Bass. The first two were taken during those relationships. The third is me four years later. I am not perfect. But I am free.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="rounded-2xl overflow-hidden shadow-card mb-4 aspect-square">
                <img src="https://customer-assets.emergentagent.com/job_2eed5c8c-c903-4cd4-a6ee-5dc5c12a3bca/artifacts/8ph8r20v_Brian5.JPG" alt="Brian during first relationship" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs font-semibold text-red-400 uppercase tracking-wider">During</p>
              <p className="text-sm text-[#1A2A3A]/60 mt-1">Lost in the chaos. I did not even recognize myself.</p>
            </div>
            <div className="text-center">
              <div className="rounded-2xl overflow-hidden shadow-card mb-4 aspect-square">
                <img src="https://customer-assets.emergentagent.com/job_2eed5c8c-c903-4cd4-a6ee-5dc5c12a3bca/artifacts/8ut5ksjk_CDbaby%20artwork3.jpg" alt="Brian during second relationship" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs font-semibold text-red-400 uppercase tracking-wider">During</p>
              <p className="text-sm text-[#1A2A3A]/60 mt-1">The darkness was real. But so was the fight inside me.</p>
            </div>
            <div className="text-center">
              <div className="rounded-2xl overflow-hidden shadow-card mb-4 aspect-square border-2 border-[#4FB3D4]/30">
                <img src="https://customer-assets.emergentagent.com/job_2eed5c8c-c903-4cd4-a6ee-5dc5c12a3bca/artifacts/oyfd26yh_IMG_5012.jpeg" alt="Brian today - 4 years later" className="w-full h-full object-cover object-top" />
              </div>
              <p className="text-xs font-semibold text-green-500 uppercase tracking-wider">4 Years Later</p>
              <p className="text-sm text-[#1A2A3A]/60 mt-1">Still growing. Still learning. But finally free.</p>
            </div>
          </div>
          <p className="text-center text-xs text-[#1A2A3A]/40 mt-8 italic">Incremental Steps. Exponential Thryving. — Brian Bass (BriVonB)</p>
        </div>
      </section>

      {/* My Story Section */}
      <section className="py-24 px-6 lg:px-12 bg-white" data-testid="my-story">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-4">
              My Story
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#1A2A3A]">
              From Survivor to Thriver
            </h2>
          </div>

          <div className="space-y-8 text-lg text-[#1A2A3A]/70 leading-relaxed">
            <p>
              For years, I found myself trapped in a cycle of manipulation, gaslighting, and emotional abuse — two back-to-back narcissistic relationships that nearly broke me. I questioned my own reality, lost touch with who I was, and felt completely alone even when surrounded by people. The cheating, the lies, the control. I did not know the words for what was happening to me. I just knew I was drowning.
            </p>
            <p>
              The turning point came quietly. I was sitting on a bar bench in the country, building my mother a home — a project that took two years. She had purchased a desktop computer that had been sitting there for about a year before I finally got to it. When I did, I tore into it. I searched how to label, disassemble, and reassemble every part — every pin, every connection. Something clicked. Not just the computer. Something inside me.
            </p>
            <p>
              At the time, I was playing music in Hot Springs, Arkansas. After a series of rather difficult moments — the kind that make you question everything — I found myself studying affiliate marketing. I never finished the training because I could not find the right mix of programs that made sense. Then AI showed up and gave us all a taste for free. I was probably among the first 100,000 people to sign up and use it, and I was completely clueless. But I kept at it. Day after day. Incremental steps.
            </p>
            <p>
              Today, I build apps and websites, digital products to sell for profit, chatbots, autobots, thinking bots, research bots — tools that actually help people. I went from not knowing how to turn on a desktop to creating platforms like the one you are looking at right now. That is what recovery looks like. It is not a straight line. It is messy, it is slow, and some days it feels impossible. But it is real.
            </p>
            <p>
              I built ThryvLife because I needed it to exist. Every tool on this platform — the AI companion, the Pattern Detector, the PDFs, the data, the community — was created by someone who has been exactly where you are. I am not a therapist. I am not perfect. I am a human who chose to rebuild, and I am still building every single day.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#FDFBF7] to-[#DCD6F7]/20" data-testid="values-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#4FB3D4] mb-4">
              My Approach
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#1A2A3A]">
              Guiding Principles
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-card p-8 text-center card-hover">
              <div className="w-16 h-16 rounded-2xl bg-[#FFB7B2]/20 flex items-center justify-center mx-auto mb-6">
                <Heart className="h-8 w-8 text-[#FFB7B2]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-3">
                Compassion First
              </h3>
              <p className="text-[#1A2A3A]/60">
                Healing happens in a space of unconditional acceptance. No judgment, just support.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-card p-8 text-center card-hover">
              <div className="w-16 h-16 rounded-2xl bg-[#4FB3D4]/10 flex items-center justify-center mx-auto mb-6">
                <Star className="h-8 w-8 text-[#4FB3D4]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-3">
                Evidence-Based
              </h3>
              <p className="text-[#1A2A3A]/60">
                Every resource is grounded in psychology and proven recovery methods.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-card p-8 text-center card-hover">
              <div className="w-16 h-16 rounded-2xl bg-[#A69BE0]/20 flex items-center justify-center mx-auto mb-6">
                <Lightbulb className="h-8 w-8 text-[#A69BE0]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-3">
                Empowerment
              </h3>
              <p className="text-[#1A2A3A]/60">
                My goal is to help you reclaim your power and build the life you deserve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-white" data-testid="about-cta">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#1A2A3A] mb-6">
            Ready to Begin?
          </h2>
          <p className="text-lg text-[#1A2A3A]/60 mb-10">
            Your journey to healing starts with a single step. We are here to walk alongside you. No promises of cures. Just real tools from a real human who understands.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/checkout">
              <Button 
                className="btn-primary text-lg px-8 py-6 rounded-full font-semibold"
                data-testid="about-cta-btn"
              >
                Start Your Recovery
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/chat">
              <Button 
                variant="outline" 
                className="rounded-full px-8 py-6 text-lg font-medium border-2 border-[#4FB3D4]/20 hover:bg-[#4FB3D4]/5"
              >
                Talk to Hope (Free)
              </Button>
            </Link>
            <Link to="/resources">
              <Button 
                variant="outline" 
                className="rounded-full px-8 py-6 text-lg font-medium border-2 border-[#FFB7B2]/20 hover:bg-[#FFB7B2]/5"
              >
                Emergency Resources
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-heading text-2xl font-bold">ThryvLife</div>
            <nav className="flex gap-8">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <Link to="/checkout" className="text-white/70 hover:text-white transition-colors">Pricing</Link>
              <Link to="/bonus" className="text-white/70 hover:text-white transition-colors">Resources</Link>
            </nav>
            <p className="text-white/50 text-sm">© 2025 ThryvLife. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
